import { useEffect, useRef, useState } from "react"
import {
  HALO_PAD,
  MIX_TAU,
  drawHaloFrame,
  haloTimeScale,
  iconAnchors,
  iconRadius,
  pillLoops,
  stepMix,
} from "../setup/haloGeometry"

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches
}

function sceneFor(canvas, width, height, mix) {
  const cssWidth = width + HALO_PAD * 2
  const cssHeight = height + HALO_PAD * 2
  const dpr = Math.min(window.devicePixelRatio || 1, 2)
  canvas.style.width = `${cssWidth}px`
  canvas.style.height = `${cssHeight}px`
  canvas.width = Math.round(cssWidth * dpr)
  canvas.height = Math.round(cssHeight * dpr)
  const loops = pillLoops(width, height, HALO_PAD)
  return {
    ctx: canvas.getContext("2d"),
    cssWidth,
    cssHeight,
    dpr,
    loops,
    anchors: iconAnchors(HALO_PAD, HALO_PAD, width, height),
    radius: iconRadius(width),
    timeScale: haloTimeScale(loops[0].length),
    mix,
  }
}

export default function ButtonHalo({ lit, targetRef }) {
  const canvasRef = useRef(null)
  const sceneRef = useRef(null)
  const litRef = useRef(lit)
  const [reduced, setReduced] = useState(prefersReducedMotion)
  const [ready, setReady] = useState(false)
  litRef.current = lit

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)")
    const onChange = () => setReduced(query.matches)
    query.addEventListener("change", onChange)
    return () => query.removeEventListener("change", onChange)
  }, [])

  useEffect(() => {
    if (reduced) return undefined
    const target = targetRef.current
    const canvas = canvasRef.current
    if (!target || !canvas) return undefined
    const measure = () => {
      const rect = target.getBoundingClientRect()
      if (rect.width < 1 || rect.height < 1) return
      sceneRef.current = sceneFor(canvas, rect.width, rect.height, sceneRef.current?.mix ?? 0)
      setReady(true)
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(target)
    return () => {
      observer.disconnect()
      sceneRef.current = null
      setReady(false)
    }
  }, [reduced, targetRef])

  useEffect(() => {
    if (reduced || !ready) return undefined
    let frame = 0
    let last = performance.now()
    const tick = (now) => {
      const scene = sceneRef.current
      if (!scene) return
      const dt = Math.min(0.05, Math.max(0, (now - last) / 1000))
      last = now
      const target = litRef.current ? 1 : 0
      scene.mix = stepMix(scene.mix, target, dt, MIX_TAU)
      drawHaloFrame(scene.ctx, {
        cssWidth: scene.cssWidth,
        cssHeight: scene.cssHeight,
        dpr: scene.dpr,
        loops: scene.loops,
        anchors: scene.anchors,
        radius: scene.radius,
        mix: scene.mix,
        time: (now / 1000) * scene.timeScale,
      })
      if (litRef.current || scene.mix > 0.004) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [lit, ready, reduced])

  if (reduced) return null

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute"
      style={{ left: -HALO_PAD, top: -HALO_PAD }}
      aria-hidden="true"
    />
  )
}
